import React, { createContext, useContext, useState, useEffect, useCallback, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import axios from "axios";

// Core pages loaded eagerly (no heavy libs)
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import DashboardPage from "@/pages/DashboardPage";
import PersonalDashboardPage from "@/pages/PersonalDashboardPage";
import SubjectPage from "@/pages/SubjectPage";

// Pages with heavy libs (KaTeX/Mermaid) - lazy loaded
const ChapterPage = lazy(() => import("@/pages/ChapterPage"));
const DoubtPage = lazy(() => import("@/pages/DoubtPage"));
const TestPage = lazy(() => import("@/pages/TestPage"));
const HomeworkSolverPage = lazy(() => import("@/pages/HomeworkSolverPage"));
const ExamCountdownPage = lazy(() => import("@/pages/ExamCountdownPage"));
const FlashcardsPage = lazy(() => import("@/pages/FlashcardsPage"));
const AdminPage = lazy(() => import("@/pages/AdminPage"));
const PolicyPage = lazy(() => import("@/pages/PolicyPage"));

import GlobalReportButton from "@/components/GlobalReportButton";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { Toaster } from "@/components/ui/sonner";

// Register service worker
if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => {});
  });
}

// Track PWA sessions and installs (when opened as installed app)
if (window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true) {
  const userId = localStorage.getItem("cached_user") ? JSON.parse(localStorage.getItem("cached_user"))?.id : "anonymous";
  // Track session
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pwa/track-session`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ user_id: userId })
  }).catch(() => {});
  // Track install once (iOS has no install event, so we detect standalone mode)
  if (!localStorage.getItem("pwa_install_tracked")) {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/pwa/track-install`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ platform: navigator.userAgent.match(/iPhone|iPad|iPod/i) ? "ios" : "android", user_agent: navigator.userAgent })
    }).then(() => localStorage.setItem("pwa_install_tracked", "1")).catch(() => {});
  }
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Auth Context
const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

function AuthProvider({ children }) {
  // Load cached user immediately so UI renders instantly
  const [user, setUser] = useState(() => {
    try {
      const cached = localStorage.getItem("cached_user");
      return cached ? JSON.parse(cached) : null;
    } catch { return null; }
  });
  const [token, setToken] = useState(localStorage.getItem("token"));
  const [loading, setLoading] = useState(!localStorage.getItem("cached_user"));
  const [serverDown, setServerDown] = useState(false);

  const fetchUser = useCallback(async (retries = 1) => {
    if (!token) {
      setLoading(false);
      setUser(null);
      return;
    }
    try {
      const res = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      setUser(res.data);
      localStorage.setItem("cached_user", JSON.stringify(res.data));
      setServerDown(false);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem("token");
        localStorage.removeItem("cached_user");
        setToken(null);
        setUser(null);
      } else if (retries > 0) {
        setServerDown(true);
        await new Promise(r => setTimeout(r, 2000));
        return fetchUser(retries - 1);
      } else {
        setServerDown(true);
      }
    }
    setLoading(false);
  }, [token]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const login = (tokenVal, userData) => {
    localStorage.setItem("token", tokenVal);
    localStorage.setItem("cached_user", JSON.stringify(userData));
    setToken(tokenVal);
    setUser(userData);
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("cached_user");
    setToken(null);
    setUser(null);
  };

  const updateGrade = async (grade) => {
    try {
      // If switching to 11-12 and no stream set, default to Science
      const updates = { grade };
      if (grade >= 11 && !user?.stream) {
        updates.stream = "Science";
      }
      // If switching from 11-12 to lower, clear stream
      if (grade < 11) {
        updates.stream = "";
      }
      const res = await axios.put(`${API}/auth/profile`, updates, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const updateBoard = async (board) => {
    try {
      const res = await axios.put(`${API}/auth/profile`, { board }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const updateStream = async (stream) => {
    try {
      const res = await axios.put(`${API}/auth/profile`, { stream }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, serverDown, login, logout, updateGrade, updateBoard, updateStream, retryConnection: () => fetchUser(3) }}>
      {children}
    </AuthContext.Provider>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading, serverDown, token, retryConnection } = useAuth();
  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #0F1B3D 0%, #0D1733 100%)' }}>
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-blue-500/30 border-t-orange-500 rounded-full animate-spin mx-auto mb-4" />
        <p className="text-white/50 font-body">{serverDown ? "Server is starting up..." : "Loading..."}</p>
      </div>
    </div>
  );
  // If server is down but user has a saved token, show retry screen instead of login
  if (!user && token && serverDown) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #0F1B3D 0%, #0D1733 100%)' }}>
      <div className="text-center max-w-sm mx-auto px-4">
        <div className="w-16 h-16 rounded-2xl bg-orange-500/20 flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-orange-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
        </div>
        <h2 className="text-white font-heading font-bold text-xl mb-2">Server is waking up</h2>
        <p className="text-white/50 text-sm mb-6">This takes about 10-15 seconds. You are still logged in.</p>
        <button onClick={retryConnection} className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-6 py-3 rounded-full transition-all active:scale-95">
          Try Again
        </button>
      </div>
    </div>
  );
  if (!user) return <Navigate to="/auth" />;
  return children;
}

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(180deg, #0F1B3D 0%, #0D1733 100%)' }}>
    <div className="w-10 h-10 border-4 border-blue-500/30 border-t-orange-500 rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth" element={<AuthPage />} />
            <Route path="/home" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><PersonalDashboardPage /></ProtectedRoute>} />
            <Route path="/subject/:subjectId" element={<ProtectedRoute><SubjectPage /></ProtectedRoute>} />
            <Route path="/chapter/:chapterId" element={<ProtectedRoute><ChapterPage /></ProtectedRoute>} />
            <Route path="/doubt" element={<ProtectedRoute><DoubtPage /></ProtectedRoute>} />
            <Route path="/quiz" element={<Navigate to="/test" replace />} />
            <Route path="/quiz/:subjectId" element={<Navigate to="/test" replace />} />
            <Route path="/test" element={<ProtectedRoute><TestPage /></ProtectedRoute>} />
            <Route path="/homework" element={<ProtectedRoute><HomeworkSolverPage /></ProtectedRoute>} />
            <Route path="/exam-countdown" element={<ProtectedRoute><ExamCountdownPage /></ProtectedRoute>} />
            <Route path="/flashcards" element={<ProtectedRoute><FlashcardsPage /></ProtectedRoute>} />
            <Route path="/adminwaysonly" element={<AdminPage />} />
            <Route path="/privacy" element={<PolicyPage />} />
            <Route path="/terms" element={<PolicyPage />} />
            <Route path="/refund" element={<PolicyPage />} />
            <Route path="/about" element={<PolicyPage />} />
            <Route path="/contact" element={<PolicyPage />} />
          </Routes>
        </Suspense>
        <GlobalReportButton />
        <PWAInstallPrompt />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
